import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import PtfButton from "@/components/PtfButton";
import { useMemberAuth } from "@/context/MemberAuthContext";
import { api } from "@/lib/api";
import AuthForms from "@/pages/membership/AuthForms";
import DawnWizard from "@/pages/dawn/DawnWizard";

function AlreadySubmittedScreen({ app }) {
  return (
    <div data-testid="dawn-already-submitted" className="bg-white border border-ptf-primary/12 p-8 md:p-12 max-w-xl">
      <CheckCircle2 className="h-10 w-10 text-emerald-600" />
      <p className="mt-4 text-[0.65rem] uppercase tracking-[0.2em] text-ptf-text/50">DAWN: A Project of Belief</p>
      <h2 className="font-display uppercase text-3xl tracking-tight text-ptf-primary">Application Received</h2>
      {app.application_number && <p className="mt-2 text-sm text-ptf-text/70">Application ID: <strong>{app.application_number}</strong></p>}
      <p className="mt-5 text-sm text-ptf-text/70 leading-relaxed">
        Your application is under evaluation. Status:{" "}
        <span className="font-semibold text-ptf-primary" data-testid="dawn-status">{app.status}</span>.
        You do not need to apply or pay again.
      </p>
      <PtfButton to="/account" className="mt-6">Go to My Dashboard</PtfButton>
    </div>
  );
}

export default function DawnApply() {
  const { member, ready } = useMemberAuth();
  const queryClient = useQueryClient();
  const { data: app, isLoading } = useQuery({
    queryKey: ["dawn-application"],
    queryFn: async () => (await api.get("/member/dawn/application")).data,
    enabled: !!member,
  });

  return (
    <>
      <Seo title="Apply for DAWN: A Project of Belief" path="/dawn/apply" description="Apply for DAWN: A Project of Belief. Answer questions of belief, submit a short on-camera video and enter the selection process for the 27 February to 13 March 2027 journey." />
      <PageHero
        eyebrow="DAWN: A Project of Belief"
        titleLines={["Apply for", "DAWN."]}
        intro="Nine questions of belief and one 60 to 90 second on-camera video. 24 participants selected by the Foundation for the 27 February to 13 March 2027 journey."
        image="https://customer-assets-gfyr7b9c.emergentagent.net/job_planet-purpose/artifacts/0la6er80_05.webp"
      />
      <section className="mx-auto max-w-[900px] px-5 sm:px-8 py-16 md:py-24">
        <div className="mb-10 border border-ptf-primary/12 bg-ptf-primary/[0.03] p-5 text-xs text-ptf-text/65 leading-relaxed space-y-2" data-testid="dawn-fee-info">
          <p>The application fee is <strong>&#8377;499</strong>, payable after you accept the declarations below. It is non-refundable regardless of the outcome of your application.</p>
          <p>Submitting an application does not guarantee shortlisting, an interview or selection. No fixed metrics or scarcity claims apply beyond what is stated here: 24 participants will be selected.</p>
          <p>
            Please read the official{" "}
            <Link to="/dawn/terms" target="_blank" rel="noopener noreferrer" data-testid="dawn-terms-link-apply" className="underline font-semibold text-ptf-secondary hover:text-ptf-primary">
              Terms &amp; Conditions
            </Link>{" "}
            before applying.
          </p>
        </div>
        {!ready || (member && isLoading) ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-ptf-primary" /></div>
        ) : !member ? (
          <div data-testid="dawn-auth-gate">
            <p className="text-sm text-ptf-text/70 mb-6">Sign in or create a free Planet Trotter account to start your application.</p>
            <AuthForms requireIntent={false} />
          </div>
        ) : !app ? (
          <div className="text-sm text-ptf-text/60">Could not load your application. Please refresh the page or sign in again.</div>
        ) : app.status === "Draft" || app.status === "Paid" ? (
          <DawnWizard app={app} onUpdated={() => queryClient.invalidateQueries({ queryKey: ["dawn-application"] })} />
        ) : (
          <AlreadySubmittedScreen app={app} />
        )}
      </section>
    </>
  );
}
